import { lazy, type ReactNode } from 'react';
import { Route } from 'react-router-dom';
import {
  RemountBoundary,
  RouteErrorFallback,
} from '../../shared/components/ErrorBoundary/index.js';
import { ErrorIsolationProbe } from '../../test/e2e/errorIsolationProbe.js';
import { withRegisteredScreen } from './registryGuard.js';

const RecoveryHost = lazy(() =>
  import('../../shared/recovery/RecoveryHost.js').then((module) => ({
    default: module.RecoveryHost,
  })),
);

function RecoverySuspense({ children }: { children: ReactNode }): JSX.Element {
  return <>{children}</>;
}

/**
 * L3-equivalent isolation for recovery routes.
 * Shell L2 stays disabled on recovery (recoveryPassThrough) so remount does not
 * wipe recovery chrome — this boundary catches RecoveryHost/Surface throws so they
 * do not escalate to L1 → nested unknown-error (G1).
 *
 * ## G1 — production throw sites vs L3 remount (evidence)
 *
 * Nesting (outer → inner):
 *   withRecoveryRemount(RemountBoundary) → withRegisteredScreen → RecoveryHost
 *   → RecoverySurface
 *
 * Covered INSIDE RemountBoundary (`customer-eb-l3-recovery`):
 *   - RegisteredScreen STRICT unknown `screenId` throw (registryGuard)
 *   - RecoveryHost `useTenantCode()` (TenantProvider missing → throw)
 *   - RecoverySurface `useTenantCode()` (same)
 *   - Host/Surface render tree (RecoveryLayout, ScreenState, Button)
 *   - lazy RecoveryHost render failure (caught when element evaluates under L3)
 *   - ErrorIsolationProbe (E2E-only; prod-stripped unless `__RPAPP_E2E_THROW__`)
 *
 * Outside L3 (intentional remaining bypass — shell chrome, not outlet content):
 *   - CustomerAppShell header / banners / BottomNav / ToastHost (never under outlet remount)
 *   - Parent App `<Suspense>` around Outlet (suspend, not Host render throws)
 */
export function withRecoveryRemount(element: JSX.Element): JSX.Element {
  return (
    <RemountBoundary
      feature="recovery"
      fallback={({ onRetry, feature }) => (
        <RouteErrorFallback
          onRetry={onRetry}
          feature={feature}
          testId="customer-eb-l3-recovery"
        />
      )}
    >
      <ErrorIsolationProbe feature="recovery" />
      {element}
    </RemountBoundary>
  );
}

/** Recovery routes — ADR-011 `/:tenantCode/recovery/:recoveryClass`.
 * Do NOT wrap in EntitlementRouteGuard: when the entitlement API is down the
 * guard would redirect here and then block again → blank outlet.
 *
 * Composition: withRecoveryRemount(OUTSIDE) → withRegisteredScreen(INSIDE) → Host.
 * RemountBoundary is outermost so registry STRICT throws cannot bypass L3 (G1).
 */
export function RecoveryRoutes(): JSX.Element {
  return (
    <Route
      path="recovery/:recoveryClass"
      element={withRecoveryRemount(
        withRegisteredScreen(
          'MS-REC-070',
          <RecoverySuspense>
            <RecoveryHost />
          </RecoverySuspense>,
        ),
      )}
    />
  );
}
